<template>
    <!--conteneur principal-->
    <v-card flat
            class="my-4"
            v-bind:class="isMobile ? ['rounded-lg', 'mx-4'] : ['rounded-xl', 'mx-7', 'px-5']"
            :loading="!reportDataReady"
            role="region"
            :aria-label="$t('accessibility.documentList')"
            :aria-busy="!reportDataReady ? 'true' : 'false'">

        <!--1er panel avec le titre -->
        <v-card-title class="card-title font-weight-medium">
            <v-row justify="space-between" class="ma-1 flex-nowrap">

                <span class="better-break" role="heading" aria-level="1">
                    {{getTitle}}
                </span>

                <v-hover v-slot="{hover}">
                    <v-btn 
                        icon
                        @click="close"
                        :color="hover ? 'linkHover' : 'textColor'"
                        :aria-label="$t('accessibility.closeDocumentList')">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-hover>

            </v-row>
        </v-card-title>
        <v-card-subtitle>
            <!-- Module de recherche -->
            <!--2ieme panel-->
            <v-row v-if="!isMobile" justify="space-between">
                <v-col cols="12">
                    <v-form 
                        ref="searchform" 
                        v-model="searchValid" 
                        lazy-validation
                        role="search"
                        :aria-label="$t('accessibility.documentSearch')">
                        <v-row align="center" class="my-2">
                            <QuickSearch :displayColumns="displayColumns"
                                         :user="user"
                                         :reportCode="reportCode"
                                         @search="onQuickSearch"></QuickSearch>
                            <v-spacer></v-spacer>

                            <div class="d-flex flex-column align-end">
                                <v-menu v-model="menu"
                                        :close-on-content-click="false"
                                        bottom
                                        offset-y>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-hover v-slot="{hover}">
                                            <v-btn class="text-none ma-2 mr-4"
                                                   :color="hover ? 'buttonHover' : 'primary'"
                                                   dark depressed
                                                   v-bind="attrs"
                                                   v-on="on"
                                                   @click="openSearch"
                                                   :aria-label="$t('dashboard-view.advancedSearch')"
                                                   :aria-expanded="menu ? 'true' : 'false'">
                                                {{formatMenuText($t('dashboard-view.advancedSearch'))}}
                                            </v-btn>
                                        </v-hover>
                                    </template>
                                    <v-card class="my-auto overflow-y-auto overflow-x-hidden"
                                            width="400px"
                                            flat
                                            height="400px"
                                            role="dialog"
                                            :aria-label="$t('dashboard-view.advancedSearch')">
                                        <v-col class="c-menu-header"
                                               v-bind:class="advancedSearchHeaderColor ? 'c-menu-header-color' : 'c-menu-header-color-primary'">
                                            <v-row class="text-normal my-auto"
                                                   align="center"
                                                   justify="center"
                                                   role="heading"
                                                   aria-level="2">
                                                {{formatMenuText($t('dashboard-view.advancedSearch'))}}
                                            </v-row>
                                        </v-col>

                                        <v-card-text class="overflow-y-auto c-card-text">
                                            <v-list>
                                                <v-list-item v-for="(term, termIdx) in searchTermsList" :key="termIdx">
                                                    <v-list-item-content>
                                                        <v-text-field v-model="term.SearchValue"
                                                                      outlined
                                                                      dense
                                                                      :rules="inputRules"
                                                                      hide-details
                                                                      clearable
                                                                      maxlength="200"
                                                                      :label="formatMenuText($i18n.locale == 'fr' ? term.DisplayNameFR : term.DisplayNameEN)">
                                                        </v-text-field>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </v-list>
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-row align="center" justify="space-around">
                                                <v-hover v-slot="{hover}">
                                                    <v-btn class="text-none ma-2 text-small"
                                                           :color="hover ? 'buttonHover' : 'primary'"
                                                           min-width="33%"
                                                           dark depressed
                                                           @click="applySearch"
                                                           :aria-label="$t('apply')">
                                                        {{formatMenuText($t('apply'))}}
                                                    </v-btn>
                                                </v-hover>
                                                <v-hover v-slot="{hover}">
                                                    <v-btn class="text-none ma-2 text-small"
                                                           :color="hover ? 'buttonHover' : 'primary'"
                                                           min-width="33%"
                                                           dark depressed
                                                           @click="cancelSearch"
                                                           :aria-label="$t('cancel')">
                                                        {{formatMenuText($t('cancel'))}}
                                                    </v-btn>
                                                </v-hover>
                                            </v-row>
                                        </v-card-actions>
                                    </v-card>
                                </v-menu>
                                <v-hover v-if="reportData?.Config?.Client?.ActiveDocumentsExport || reportData?.Config?.Client?.activeDocumentsExport" v-slot="{hover}">
                                    <v-btn class="text-none ma-2 mr-4"
                                           :color="hover ? 'buttonHover' : 'primary'"
                                           dark
                                           depressed
                                           :loading="isExporting"
                                           @click="exportReportData"
                                           :aria-label="$t('dashboard-view.export')">
                                        {{formatMenuText($t('dashboard-view.export'))}}
                                    </v-btn>
                                </v-hover>
                            </div>
                        </v-row>
                            <v-row>
                                <span class="ml-2 my-1">
                                    <DatePicker v-model="searchDates"
                                                :minDate="searchDateMin"
                                                :locale="this.$i18n.locale"
                                                @input="refreshListOnDate"></DatePicker>
                                </span>
                                <template v-if="!isDocumentProcessing">
                                    <v-btn
                                        icon
                                        :title="$t('dashboard-view.downloadAllSelected')"
                                        :aria-label="$t('dashboard-view.downloadAllSelected')"
                                        class="pr-1"
                                        style="margin-left:15px; margin-top: 10px; margin-bottom:10px;"
                                        @click="onDownloadListDocumentClick()">
                                        <v-icon>mdi-download</v-icon>
                                    </v-btn>
                                    <v-btn
                                        icon
                                        :title="$t('dashboard-view.previewAllSelected')"
                                        :aria-label="$t('dashboard-view.previewAllSelected')"
                                        class="pr-1"
                                        style="margin-top: 10px; margin-bottom:10px;"
                                        @click="onPreviewDocumentListClick()">
                                        <v-icon>mdi-eye-outline</v-icon>
                                    </v-btn>
                                </template>
                                <v-progress-circular 
                                    style="margin-left: 15px;" 
                                    v-if="isDocumentProcessing" 
                                    indeterminate
                                    color="primary"
                                    :aria-label="$t('accessibility.processingDocuments')">
                                </v-progress-circular>
                                <span v-if="isDocumentProcessing" class="sr-only" aria-live="polite">
                                    {{$t('accessibility.processingDocuments')}}
                                </span>
                                <v-spacer></v-spacer>
                                <template v-for="(searchOption) in advancedSearchOptions">
                                    <v-tooltip top :key="searchOption.searchTerms">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip class="ma-1"
                                                    color="primary lighten-2"
                                                    close
                                                    @click:close="removeSearchOption(searchOption)"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :aria-label="$t('accessibility.activeFilter', { filter: searchOption.searchValue })">
                                                {{searchOption.searchValue}}
                                            </v-chip>
                                        </template>
                                        <span>{{searchName(searchOption.searchTerms)}}</span>
                                    </v-tooltip>
                                </template>
                            </v-row>
                    </v-form>
                </v-col>
            </v-row>
            <!-- 2ieme panel Mobile -->
            <v-container v-if="isMobile">
                <v-form ref="searchform" v-model="searchValid" lazy-validation>
                    <v-row>
                        <QuickSearch :displayColumns="displayColumns"
                                     :user="user"
                                     :reportCode="reportCode"
                                     @search="onQuickSearch"></QuickSearch>
                    </v-row>
                    <v-row>
                        <span class="d-inline-flex ma-2">
                            <DatePicker v-model="searchDates"
                                        :minDate="searchDateMin"
                                        :locale="this.$i18n.locale"
                                        @input="refreshListOnDate"></DatePicker>
                        </span>
                        <v-spacer></v-spacer>
                        <template v-for="(searchOption) in advancedSearchOptions">
                            <v-tooltip top :key="searchOption.searchTerms">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip class="ma-1"
                                            color="primary lighten-2"
                                            close
                                            @click:close="removeSearchOption(searchOption)"
                                            v-bind="attrs"
                                            v-on="on">
                                        {{searchOption.searchValue}}
                                    </v-chip>
                                </template>
                                <span>{{searchName(searchOption.searchTerms)}}</span>
                            </v-tooltip>
                        </template>
                    </v-row>
                </v-form>
            </v-container>
            <!-- Fin Mobile -->
        </v-card-subtitle>

        <!--3ieme panel tableau des documents-->
        <v-card-text :class="isMobile ? ['pa-0'] : []">
            <v-data-table v-if="!isMobile"
                          v-model="selected"
                          :headers="headers"
                          :items="items"
                          item-key="downloadKey"
                          show-select
                          id="document--list"
                          :item-class="getItemClass"
                          :items-per-page="tableOptions.itemsPerPage"
                          :options.sync="tableOptions"
                          :header-props="{ 'sort-icon': 'mdi-triangle-small-up'} "
                          :footer-props="{
                                    disableItemsPerPage: true,
                                    itemsPerPageOptions: [10, 25, 50, 100, 200],
                                    itemsPerPageText: $t('user-management.itemsPerPage')
                                  }"
                          :server-items-length="totalDocuments"
                          :aria-label="$t('accessibility.documentsTable')">
                <template v-slot:[`header.data-table-select`]>
                    <v-checkbox 
                        color="grey"
                        @click="onSelectAll(items)"
                        :aria-label="$t('accessibility.selectAllDocuments')" />
                </template>
                <template v-slot:item.viewed="props">
                    <v-badge :color="$vuetify.theme.themes.light.linkHover"
                             dot
                             :class="props.item.viewed ? 'd-none' : ''"
                             :aria-label="props.item.viewed ? '' : $t('accessibility.unreadDocument')">
                    </v-badge>
                    <span v-if="!props.item.viewed" class="sr-only">
                        {{$t('accessibility.unreadDocument')}}
                    </span>
                </template>
                <template v-slot:item.Actions="props">
                    <v-btn
                        icon
                        small
                        @click="onDownloadDocumentClick(props.item)"
                        :aria-label="$t('accessibility.downloadDocument')">
                        <v-icon class="pr-1">mdi-download</v-icon>
                    </v-btn>
                    <v-btn
                        v-if="!props.item.previewLoading"
                        icon
                        small
                        @click="onPreviewDocumentClick(props.item)"
                        :aria-label="$t('accessibility.previewDocument')">
                        <v-icon class="pr-1">mdi-eye-outline</v-icon>
                    </v-btn>
                    <v-progress-circular
                        v-if="props.item.previewLoading"
                        indeterminate
                        size="20"
                        color="primary"
                        :aria-label="$t('accessibility.loadingPreview')">
                    </v-progress-circular>
                    <span v-if="props.item.previewLoading" class="sr-only" aria-live="polite">
                        {{$t('accessibility.loadingPreview')}}
                    </span>
                </template>
                <template #footer.page-text="props">
                    <span :aria-live="'polite'" :aria-atomic="'true'">
                        {{props.pageStart}}-{{props.pageStop}} {{$t('of').toLowerCase()}} {{props.itemsLength}}
                    </span>
                </template>
                <template v-slot:footer.prepend>
                    <div class="v-data-footer__select d-flex align-center">
                        <span class="v-data-footer__select-text mr-2">{{formatMenuText($t('user-management.itemsPerPage'))}}</span>
                        <select
                            :value="tableOptions.itemsPerPage"
                            @blur="onItemsPerPageChange($event.target.value)"
                            :aria-label="$t('user-management.itemsPerPage')"
                            class="v-data-footer__select-native"
                            style="padding: 4px 8px; border: 1px solid rgba(0,0,0,0.38); border-radius: 4px; background-color: white; cursor: pointer; font-size: 0.875rem;">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                        </select>
                    </div>
                </template>
            </v-data-table>
            <!--3ieme panel mobile -->
            <v-col v-if="isMobile" role="region" :aria-label="$t('accessibility.documentList')">
                <v-divider></v-divider>
                <v-row>
                    <template v-for="(result, idx) in items">
                        <v-col cols="12"
                               :key="idx"
                               class="document--list--item my-2"
                               role="article"
                               :aria-label="$t('accessibility.documentItem', { index: idx + 1 })">
                            <v-simple-table>
                                <template>
                                    <tr v-for="col in reportAttributeList"
                                    :key="col.Name"
                                    style="justify-items: center;"
                                    class="text-normal"
                                    tabindex="0"
                                    role="button"
                                    :aria-label="$t('accessibility.downloadDocument')"
                                    v-on:click="onDownloadDocumentClick(result)"
                                    v-on:keydown.enter="onDownloadDocumentClick(result)"
                                    v-on:keydown.space.prevent="onDownloadDocumentClick(result)">
                                        <td>
                                            <v-badge v-if="reportAttributeList.indexOf(col) === 0"
                                                     color="#E20087"
                                                     dot
                                                     :class="result.viewed ? 'd-none' : ['mr-2','my-1','ml-1']"
                                                     :aria-label="result.viewed ? '' : $t('accessibility.unreadDocument')">
                                            </v-badge>
                                            <span v-if="reportAttributeList.indexOf(col) === 0 && !result.viewed" class="sr-only">
                                                {{$t('accessibility.unreadDocument')}}
                                            </span>
                                        </td>
                                        <td class="text-left pr-1"
                                            v-bind:class="result.viewed ? 'pl-4' : 'font-weight-bold'">
                                            {{$i18n.locale == 'fr' ? col.DisplayNameFR : col.DisplayNameEN}}
                                        </td>
                                        <td class="pl-1">{{result[col.Name]}}</td>
                                    </tr>
                                </template>
                            </v-simple-table>
                        </v-col>
                    </template>
                    <v-row align="center" justify="space-between" class="ma-1">
                        <v-col class="text-small d-flex">
                            <v-row align="center" justify="center">
                                <span>
                                    {{$t('dashboard-view.itemsPerPage')}}: &nbsp;
                                </span>
                                <select
                                    v-model="tableOptions.itemsPerPage"
                                    :aria-label="$t('accessibility.selectItemsPerPage')"
                                    style="padding: 4px 8px; border: 1px solid rgba(0,0,0,0.38); border-radius: 4px; background-color: white; cursor: pointer; font-size: 0.875rem; margin-left: 4px;">
                                    <option v-for="option in pagesOptions" :key="option" :value="option">{{option}}</option>
                                </select>
                            </v-row>
                        </v-col>
                        <v-col class="text-small d-flex">
                            <v-row align="center" justify="center">
                                <span aria-live="polite" aria-atomic="true">
                                    Page&nbsp;{{tableOptions.page}}&nbsp;{{$t('of').toLowerCase()}}&nbsp;{{Math.ceil(totalDocuments / tableOptions.itemsPerPage)}}
                                </span>
                                <span>
                                    <v-btn
                                        icon
                                        small
                                        class="my-1 mx-4"
                                        @click="previousPage"
                                        :aria-label="$t('accessibility.previousPage')"
                                        :disabled="tableOptions.page <= 1">
                                        <v-icon small>mdi-less-than</v-icon>
                                    </v-btn>
                                    <v-btn
                                        icon
                                        small
                                        class="ma-1 ml-1"
                                        @click="nextPage"
                                        :aria-label="$t('accessibility.nextPage')"
                                        :disabled="tableOptions.page >= Math.ceil(totalDocuments / tableOptions.itemsPerPage)">
                                        <v-icon small>mdi-greater-than</v-icon>
                                    </v-btn>
                                </span>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-row>
            </v-col>
        </v-card-text>
        <v-dialog v-model="showErrorPreviewDialog" max-width="615px" persistent>
            <v-card class="pa-8 rounded-xl" flat>
                <v-alert class="my-6 rounded" type="error">{{$t('dashboard-view.previewErrorMsg')}}</v-alert>
                <v-row align="center" justify="end" class="mx-0 mt-0 mb-3">
                    <v-hover v-slot="{hover}">
                        <v-btn class="text-none text-small min-height-1"
                               text plain
                               :color="hover ? 'linkHover' : 'primary'"
                               @click="showErrorPreviewDialog = false;">{{$t('dashboard-view.btnReturnHome')}}</v-btn>
                    </v-hover>
                    <v-btn class="text-none text-normal white--text ml-4"
                           min-width="33%"
                           x-large depressed
                           color="primary"
                           @click="onPreviewDocumentClick(previewItem);">{{$t('dashboard-view.btnRetry')}}</v-btn>
                </v-row>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showErrorInvalidSelectionDialog" max-width="615px">
            <v-card class="pa-8 rounded-xl" flat>
                <v-card-title>
                    <v-row>
                        <v-col>
                            <v-row class="text-large better-break" justify="center">
                                <span>{{$t('dashboard-view.invalidSelectionTitle')}}</span>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-title>

                <v-card-text class="my-0 py-0">
                    <v-row class="ma-2">
                        <v-col>
                            <v-row class="mb-2 text-normal better-break" justify="center">
                                <span>{{$t('dashboard-view.invalidSelection')}}</span>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="pb-4">
                    <v-col cols="12">
                        <v-row justify="center">
                            <v-hover v-slot="{hover}">
                                <v-btn class="text-none text-normal white--text"
                                       :color="hover ? 'buttonHover' : 'primary'"
                                       min-width="33%"
                                       x-large depressed
                                       @click="showErrorInvalidSelectionDialog = false;">
                                    {{$t('ok')}}
                                </v-btn>
                            </v-hover>
                        </v-row>
                    </v-col>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-progress-linear v-if="!reportDataReady"
                           color="primary"
                           indeterminate
                           bottom
                           :aria-label="$t('accessibility.loadingDocuments')"
                           role="progressbar">

        </v-progress-linear>
        <div v-if="!reportDataReady" class="sr-only" aria-live="polite">
            {{$t('accessibility.loadingDocuments')}}
        </div>
    </v-card>
</template>

<script>
    import Vue from "vue";
    import DatePicker from './datePicker.vue';
    import QuickSearch from './quickSearch.vue';
    import { mapState, mapActions } from "vuex";

    export default {
        name: "documentList",
        components: { DatePicker, QuickSearch },
        props: ['reportCode', 'displayProperties', 'filterUserId'],
        // les données, pour component, une méthode qui retourne un objet
        data: function () {
            return {
                title: "dashboard-view.loading",
                advancedSearchOptions: null,
                searchOptions: null,
                reportDataReady: false,
                totalDocuments: 0,
                reportData: {},
                pagesOptions: [10, 25, 50, 100, 200],
                menu: false,
                search: "",
                searchPreference: {
                    searchMethod: 0,
                    searchColumns: [],
                    updateSearchPreference: false,
                },
                selected: [],
                searchTermsList: [],
                searchTermsListBackup: [],
                selectAll: false,
                noTermsConfFound: false,
                formProcess: false,
                searchDateMin: null,
                searchDateStart: null,
                searchDateEnd: null,
                searchDates: [],
                tableOptions: {
                    itemsPerPage: 25,
                    page: 1,
                    sortBy: [''],
                    sortDesc: [false],
                },
                searchValid: false,
                showErrorPreviewDialog: false,
                showErrorInvalidSelectionDialog: false,
                previewItem: undefined,
                isDocumentProcessing: false,
                accessibilityObserver: null,
                isFixingAccessibility: false,
                lastAccessibilityFixTime: 0,
                hasFixedAccessibilityOnce: false,
                isLoadingReportData: false,
                isExporting: false
            };
        },
        computed: {
            ...mapState("account", ["user"]),
            getTitle() {
                if (this.reportData?.Config) {
                    return this.$i18n.locale == 'fr' ? this.reportData?.Config.TitleFr : this.reportData?.Config.TitleEn;
                }
                return this.$t(this.title);
            },
            inputRules() {
                return [
                    (val) => (!val || val.length <= 200) || this.$t('search-max-200')
                ]
            },
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },
            reportAttributeList() {
                let list = [];

                if (!this.reportData?.Attributes) return;

                const attributes = this.reportData?.Attributes.filter(function (attr) {
                    return attr.ShowInColumn;
                })

                let attributeCount = Math.min(attributes.length, 5);

                for (let i = 0; i < attributeCount; i++) {
                    list.push(attributes[i]);
                }
                return list;
            },
            displayColumns() {
                let searchColumns = [];

                const attributes = this.reportData?.Attributes?.filter(function (attr) {
                    return attr.ShowInColumn;
                })

                if (attributes?.length) {
                    for (const attribute of attributes) {
                        searchColumns.push({
                            text: this.$i18n.locale == 'fr' ? attribute.DisplayNameFR : attribute.DisplayNameEN,
                            value: attribute.Name
                        });
                    }
                }
                return searchColumns;
            },
            headers() {
                let headersList = [];
                headersList.push({
                    text:'',
                    value: 'viewed',
                    sortable: true,
                    class: ['text-normal', 'font-weight-medium', 'opacity-1'],
                    width: '50'
                });

                const attributes = this.reportData?.Attributes?.filter(function (attr) {
                    return attr.ShowInColumn;
                })

                if (attributes?.length) {
                    for (const attribute of attributes) {
                        headersList.push({
                            text: this.$i18n.locale == 'fr' ? attribute.DisplayNameFR : attribute.DisplayNameEN,
                            value: attribute.Name,
                            sortable: true,
                            class: ['text-normal', 'font-weight-medium', 'opacity-1']
                        });
                    }
                    headersList.push({ text: '', value: 'Actions', sortable: false});
                }
                return headersList;
            },
            items() {
                let itemsList = [];
                if (this.reportData?.Results?.length && this.reportData?.Attributes?.length) {
                    for (let y = 0; y < this.reportData.Results.length; y++) {
                        let item = {};
                        for (let i = 0; i < this.reportData.Attributes.length; i++) {
                            if (this.reportData.Results[y][this.reportData.Attributes[i].Name] != null)
                                item[this.reportData.Attributes[i].Name] = this.reportData.Results[y][this.reportData.Attributes[i].Name].toString();
                        }
                        item['downloadKey'] = this.reportData.Results[y]['key'].toString();
                        item['viewed'] = this.reportData.Results[y]['viewed'];
                        item['definitionCode'] = this.reportData.Results[y]['definitionCode'].toString();
                        item['previewLoading'] = false;
                        itemsList.push(item);
                    }
                }
                return itemsList;
            },
            advancedSearchHeaderColor() {
                return this.$vuetify.theme.themes.light.advancedSearchHeaderColor;
            },
            isPageLoading() {
                return this.isDocumentProcessing;
            }
        },
        mounted() {
            this.reportUrl = "Dashboard/ReportData/" + this.reportCode;
            if (this.displayProperties != null) {
                let jsonProps = JSON.parse(this.displayProperties);
                this.tableOptions.sortBy = [jsonProps.sortBy];
                this.tableOptions.sortDesc = [jsonProps.sortDesc];
            }
            this.getReportData();

        },
        methods: {
            ...mapActions("account", ["updateSearchPreference"]),
            formatMenuText(text) {
                if (!text) return text;

                // Check the titleCase setting from the current experience
                const titleCase = this.$experience.titleCase !== false; // default to true if not set

                if (titleCase) {
                    // Title case: capitalize first letter of all words
                    return text.split(' ').map(word => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join(' ');
                } else {
                    // Sentence case: only capitalize first letter of first word
                    // But preserve acronyms
                    const words = text.split(' ');
                    return words.map((word, index) => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        if (index === 0) {
                            // First word: capitalize first letter
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        } else {
                            // Other words: all lowercase
                            return word.toLowerCase();
                        }
                    }).join(' ');
                }
            },
            fixAccessibilityIssues() {
                // Only run once per component lifecycle to avoid loops
                if (this.hasFixedAccessibilityOnce) {
                    return;
                }
                this.hasFixedAccessibilityOnce = true;

                // Prevent re-entrant calls
                if (this.isFixingAccessibility) return;
                this.isFixingAccessibility = true;

                try {
                    // Fix 1: Remove invalid ARIA attributes from data table footer
                    const footerSelectors = this.$el.querySelectorAll('.v-data-footer .v-input__slot[role="button"]');
                    footerSelectors.forEach(el => {
                        el.removeAttribute('role');
                        if (el.getAttribute('aria-haspopup') === 'listbox') {
                            el.removeAttribute('aria-haspopup');
                        }
                    });

                    const ariaOwnsDivs = this.$el.querySelectorAll('div[aria-owns]');
                    ariaOwnsDivs.forEach(el => {
                        const ariaOwnsValue = el.getAttribute('aria-owns');
                        if (ariaOwnsValue && !document.getElementById(ariaOwnsValue)) {
                            el.removeAttribute('aria-owns');
                        }
                        if (el.hasAttribute('aria-expanded')) {
                            el.removeAttribute('aria-expanded');
                        }
                    });

                    const inputSlots = this.$el.querySelectorAll('.v-data-footer .v-input__slot[aria-expanded]');
                    inputSlots.forEach(el => {
                        el.removeAttribute('aria-expanded');
                    });

                    // Fix 2: Add aria-label to ALL progress bars without one
                    const progressBars = this.$el.querySelectorAll('[role="progressbar"]:not([aria-label]):not([aria-labelledby])');
                    progressBars.forEach(progressBar => {
                        progressBar.setAttribute('aria-label', this.$t('accessibility.loading'));
                    });

                    // Fix 3: Add proper ARIA attributes to the actual table element
                    const tableElement = this.$el.querySelector('.v-data-table table');
                    if (tableElement) {
                        tableElement.setAttribute('aria-rowcount', this.totalDocuments);
                        tableElement.setAttribute('aria-busy', !this.reportDataReady ? 'true' : 'false');
                    }
                } finally {
                    this.isFixingAccessibility = false;
                }
            },
            getItemClass(item) {
                return item.viewed ? '' : 'font-weight-black';
            },
            refreshListOnDate(dates) {
                this.searchDateStart = dates[0];
                this.searchDateEnd = dates[1];
                this.getReportData();
            },
            getReportData(newQuery = true) {
                // Prevent infinite loop by checking if already loading
                if (this.isLoadingReportData) {
                    return;
                }

                // Set loading flag BEFORE modifying tableOptions to prevent watcher loop
                this.isLoadingReportData = true;
                this.reportDataReady = false;

                if (newQuery)
                    this.tableOptions.page = 1;

                let data = {
                    page: this.tableOptions.page,
                    perPage: this.tableOptions.itemsPerPage,
                    sortBy: (this.tableOptions.sortBy && this.tableOptions.sortBy.length >= 1) ? this.tableOptions.sortBy[0] : null,
                    sortDirection: (this.tableOptions.sortDesc && this.tableOptions.sortDesc.length >= 1 && this.tableOptions.sortDesc[0]) ? 'DESC' : 'ASC',
                    advandedSearch: this.advancedSearchOptions,
                    dateStart: this.searchDateStart,
                    dateEnd: this.searchDateEnd,
                    searchOptions: this.searchOptions,
                    updateSearchPreference: this.searchPreference.updateSearchPreference,
                    filterUserId: this.filterUserId ?? null
                };

                this.$axios.post(this.reportUrl, data)
                    .then((response) => {

                        Vue.set(this, 'reportData', response.data);

                        for (let i = 0; i < this.reportData.Results?.length; i++) {
                            let result = this.reportData.Results[i];

                            Vue.set(result, 'Checked', false);
                        }

                        // Set Initial ReportData
                        this.totalDocuments = this.reportData.TotalDocuments;

                        this.$set(this.searchDates, 0, response.data.DateStart);
                        this.$set(this.searchDates, 1, response.data.DateEnd);
                        this.searchDateMin = response.data.DateMin;

                        // sélectionne le premier attribute afficher dans le tableau pour fitlrer
                        this.reportData.Attributes.find(function (attr) {
                            return attr.ShowInColumn;
                        });

                        this.reportDataReady = true;

                        // Use $nextTick to ensure all reactive updates complete before resetting the loading flag
                        this.$nextTick(() => {
                            this.isLoadingReportData = false;
                        });

                    })
                    .catch((error) => {
                        console.log(error);
                        this.reportDataReady = true;

                        // Use $nextTick to ensure all reactive updates complete before resetting the loading flag
                        this.$nextTick(() => {
                            this.isLoadingReportData = false;
                        });
                        this.title = "dashboard-view.noDocumentFound"
                    });
            },
            close() {
                this.$emit('unselectReport', this.reportCode);
            },
            exportReportData() {
                if (this.isExporting) return;
                this.isExporting = true;

                let data = {
                    sortBy: (this.tableOptions.sortBy && this.tableOptions.sortBy.length >= 1) ? this.tableOptions.sortBy[0] : null,
                    sortDirection: (this.tableOptions.sortDesc && this.tableOptions.sortDesc.length >= 1 && this.tableOptions.sortDesc[0]) ? 'DESC' : 'ASC',
                    advandedSearch: this.advancedSearchOptions,
                    dateStart: this.searchDateStart,
                    dateEnd: this.searchDateEnd,
                    searchOptions: this.searchOptions,
                    filterUserId: this.filterUserId ?? null,
                    lang: this.$i18n.locale
                };

                this.$axios.post(`Dashboard/ExportReportData/${this.reportCode}`, data, { responseType: 'blob' })
                    .then((response) => {
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(new Blob([response.data]));
                        link.setAttribute('download', `${this.reportCode}.xlsx`);
                        link.click();
                    })
                    .catch((error) => {
                        console.log(error);
                    })
                    .finally(() => {
                        this.isExporting = false;
                    });
            },
            openSearch() {
                // If searchTermsList is empty
                if (!this.searchTermsList.length) {
                    this.getSearchTerms();
                }
                this.searchTermsListBackup = structuredClone(this.searchTermsList);
            },
            cancelSearch() {
                this.searchTermsList = structuredClone(this.searchTermsListBackup);
                this.menu = false;
            },
            applySearch() {
                this.menu = false;
                this.searching();
            },
            removeSearchOption(searchOption) {
                let searchTerm = this.searchTermsList.find(x => x.Attribute === searchOption.searchTerms);
                searchTerm.SearchValue = '';

                this.advancedSearchOptions.splice(this.advancedSearchOptions.indexOf(searchOption), 1);

                this.searching();
            },
            onPreviewDocumentClick(item) {
                item.previewLoading = true;

                this.showErrorPreviewDialog = false;
                this.previewItem = item;

                let documentKey = {
                    Id: item['downloadKey'],
                    DefinitionCode: item['definitionCode'],
                    Lang: this.$i18n.locale
                };
                let keyData = btoa(`${JSON.stringify(documentKey)}`);
                let documentData = `dashboard/preview?documentKey=${keyData}`;
                let favicon = this.$experience.favicon ? btoa(`./assets/Branding/${this.$experience.key}/Images/${this.$experience.favicon}`) : '';
                let branding = btoa(this.$experience.appTitle || "Vault");

                this.$axios.get(documentData, { responseType: 'blob' })
                    .then((response) => {
                        item.previewLoading = false;
                        if (!response.data.success && response.data.error == 'session-expired') {
                            this.$router.push({ name: 'login' });
                        }
                        const type = response.headers['content-type'] || null
                        const relType = 'application/pdf'

                        if (type.includes(relType)) {
                            let keyDataPDF = btoa(URL.createObjectURL(response.data));
                            globalThis.open(`preview.html?document=${keyDataPDF}&fav=${favicon}&branding=${branding}`, '_blank');

                        } else {
                            this.showErrorPreviewDialog = true;
                        }
                    })
                    .catch((error) => {
                        item.previewLoading = false;
                        this.showErrorPreviewDialog = true;
                        console.log(`Error Preview: ${error}`);
                    });

                item["viewed"] = true;
            },
            onPreviewDocumentListClick() {
                this.showErrorPreviewDialog = false;
                this.showErrorInvalidSelectionDialog = false;

                if (this.selected.length <= 0 || this.selected.length > 50) {
                    this.showErrorInvalidSelectionDialog = true;

                }
                else {
                    this.isDocumentProcessing = true;
                    let selectedList = new Array();
                    let definitionCode = this.selected[0].definitionCode;

                    for (let item of this.selected) {
                        selectedList.push(item['downloadKey']);
                    }

                    let keyData = btoa(`${JSON.stringify({ DefinitionCode: definitionCode, IdList: selectedList })}`);
                    let downloadUrl = "dashboard/previewlist";
                    let favicon = this.$experience.favicon ? btoa(`./assets/Branding/${this.$experience.key}/Images/${this.$experience.favicon}`) : '';
                    let branding = btoa(this.$experience.appTitle || "Vault");

                    this.$axios.post(downloadUrl, { documentKeyList: keyData }, { responseType: 'blob' })
                        .then((response) => {
                            this.isDocumentProcessing = false;

                            if (!response.data.success && response.data.error == 'session-expired') {
                                this.$router.push({ name: 'login' });
                            }

                            const type = response.headers['content-type'] || null;
                            const relType = 'application/pdf';


                            if (type.includes(relType)) {
                                let keyDataPDF = btoa(URL.createObjectURL(response.data));
                                window.open(`preview.html?document=${keyDataPDF}&fav=${favicon}&branding=${branding}`, '_blank');

                            } else {
                                this.showErrorPreviewDialog = true;
                            }
                        })
                        .catch((error) => {

                            this.showErrorPreviewDialog = true;
                            console.log(`Error Preview: ${error}`);
                        });

                    for (let item of this.selected) {
                        item["viewed"] = true;
                    };
                }
            },
            onDownloadDocumentClick(item) {
                let documentKey = {
                    Id: item['downloadKey'],
                    DefinitionCode: item['definitionCode'],
                    Lang: this.$i18n.locale
                };
                let keyData = btoa(JSON.stringify(documentKey))
                let downloadBaseUrl = "dashboard/download",
                    downloadUrl = `${downloadBaseUrl}?documentKey=${keyData}`;

                globalThis.location = downloadUrl;
            },
            onDownloadListDocumentClick() {
                this.showErrorPreviewDialog = false;
                this.showErrorInvalidSelectionDialog = false;

                if (this.selected.length <= 0 || this.selected.length > 50) {
                    this.showErrorInvalidSelectionDialog = true;

                }
                else {
                    let selectedList = new Array();
                    let definitionCode = this.selected[0].definitionCode;

                    for (let item of this.selected) {
                        selectedList.push(item['downloadKey']);
                    };

                    let keyData = btoa(`${JSON.stringify({ DefinitionCode: definitionCode, IdList: selectedList })}`);
                    let downloadBaseUrl = "dashboard/downloadList";

                    this.$axios.post(downloadBaseUrl, { documentKeyList: keyData } , { responseType: 'blob' })
                        .then((response) => {
                            this.isDocumentProcessing = false;

                            if (!response.data.success && response.data.error == 'session-expired') {
                                this.$router.push({ name: 'login' });
                            }

                            const type = response.headers['content-type'] || null;
                            const relType = 'application/pdf';


                            if (type.includes(relType)) {
                                let contentDisposition = response.headers["content-disposition"];
                                let filename = "merged.pdf";
                                try {
                                    let match = contentDisposition.match(/filename\s*=\s*["{1}]?([^";]+)["{1}]?[;{1}]?/i);
                                    filename = match[1];
                                } finally {
                                    const url = globalThis.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', filename);
                                    document.body.appendChild(link);
                                    link.click();
                                }
                            } else {
                                this.showErrorPreviewDialog = true;
                            }
                        })
                        .catch((error) => {

                            this.showErrorPreviewDialog = true;
                            console.log(`Error Preview: ${error}`);
                        });

                    for (let item of this.selected) {
                        item["viewed"] = true;
                    };
                }
            },
            nextPage() {
                let newPage = this.tableOptions.page + 1;
                this.tableOptions.page = Math.min(newPage, this.totalDocuments);
            },
            previousPage() {
                let newPage = this.tableOptions.page - 1;
                this.tableOptions.page = Math.max(newPage, 1);
            },
            getSearchTerms() {
                const url = "Dashboard/GetSearchAttribute" + "/" + this.reportData.Config.Id;

                this.$axios.post(url)
                    .then((response) => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            this.$router.push({ name: 'login' });
                        }
                        if (response.data.ShowAll) {
                            // if ADMIN or SUPER_USER show all search attributes
                            // Builing the searchTermsList
                            for (const attr of this.reportData.Config.Attributes) {
                                let attribute = attr["Attribute"],
                                    displayName = attr["DisplayName"],
                                    searchTerm = { Attribute: attribute, DisplayName: displayName, SearchMethod: 1, SearchValue: '' };
                                this.searchTermsList.push(searchTerm);
                            }
                        } else {
                            this.noTermsConfFound = response.data.SearchTerms.length == 0;

                            if (!this.noTermsConfFound) {
                                this.searchTermsList = response.data.SearchTerms;
                            }
                        }

                    })
                    .catch((error) => {
                        console.log(error);
                    });
            },
            searchName(term) {
                let index = this.reportData.Config.DisplayedAttributes.findIndex(x => x.Name == term);
                if (index >= 0) {
                    let displayName;
                    if (this.$i18n.locale === 'fr')
                        displayName = this.reportData.Config.DisplayedAttributes[index].DisplayNameFR;
                    else
                        displayName = this.reportData.Config.DisplayedAttributes[index].DisplayNameEN;
                    return this.formatMenuText(displayName);
                }
                return this.formatMenuText(term);
            },
            searching() {
                this.$refs.searchform.validate();
                if (this.searchValid) {
                    this.$refs.searchform.resetValidation();
                    const advancedOptionsList = new Array(),
                        searchOptionsList = new Array();

                    this.formProcess = true;

                    for (const searchTerm of this.searchTermsList) {
                        if (searchTerm.SearchValue?.length) {
                            advancedOptionsList.push({
                                searchMethod: searchTerm.SearchMethod,
                                searchTerms: searchTerm.Attribute,
                                searchValue: searchTerm.SearchValue,
                            });
                        }
                    }

                    this.advancedSearchOptions = advancedOptionsList;


                    for (const searchColumn of this.searchPreference.searchColumns) {
                        searchOptionsList.push({
                            searchMethod: this.searchPreference.searchMethod,
                            searchTerms: searchColumn,
                            searchValue: this.search,
                        });
                    }


                    this.searchOptions = searchOptionsList;

                    this.getReportData();
                    this.formProcess = false;
                }
            },
            onQuickSearch(searchParams) {
                this.search = searchParams.searchValue;
                this.searchPreference.searchMethod = Number.parseInt(searchParams.searchMethod);
                this.searchPreference.searchColumns = searchParams.searchColumns;
                this.searchPreference.updateSearchPreference = searchParams.updateSearchPreference;
                if (this.searchPreference.updateSearchPreference)
                    this.updateSearchPreference({ searchPreference: searchParams, reportCode: this.reportCode });

                if (this.search != "") {
                    this.searching();
                }
                else {
                    this.searchOptions = null;
                    if (this.displayProperties != null) {
                        let jsonProps = JSON.parse(this.displayProperties);
                        this.tableOptions.sortBy = [jsonProps.sortBy];
                        this.tableOptions.sortDesc = [jsonProps.sortDesc];
                    }
                    this.getReportData();
                }
            },
            onClearSearch() {
                this.search = '';
                this.$nextTick(() => {
                    this.$refs['searchField'].blur()
                });
            },
            onSelectAll(itemList) {
                if (this.selectAll) {
                    this.selected = [];
                    this.selectAll = false;
                }
                else {
                    if (itemList) {
                        this.selected = [];
                        let maxSelection = 50;
                        if (this.items.length < 50) {
                            maxSelection = this.items.length;
                        }

                        for (let i = 0; i < maxSelection; i++) {
                            this.items[i].selected = true;
                            this.selected.push(this.items[i]);
                        }
                    }
                    this.selectAll = true;
                }
            },
            onItemsPerPageChange(value) {
                this.tableOptions.itemsPerPage = value;
                this.tableOptions.page = 1;
            }
        },
        watch: {
            tableOptions: {
                deep: true,
                handler() {
                    if (this.reportDataReady && !this.isLoadingReportData)
                     this.getReportData(false);
                }
            },
            displayProperties: function() {
                if (this.displayProperties) {
                    this.tableOptions.sortBy = [''];
                    this.tableOptions.sortDesc = [false];
                } else {
                    let jsonProps = JSON.parse(this.displayProperties);
                    this.tableOptions.sortBy = [jsonProps.sortBy];
                    this.tableOptions.sortDesc = [jsonProps.sortDesc];
                }
                this.getReportData();
            },
            searchDates: {
                deep: true,
                handler() {
                    this.searchDateStart = this.searchDates[0];
                    this.searchDateEnd = this.searchDates[1];
                }
            }
        },
    }
</script>

<style scoped>
    .c-menu-header {
        display: block;
        height: 48px;
        color: white;
        margin: 0;
    }
    .c-menu-header-color-primary {
        background-color: var(--primary);
    }
    .c-menu-header-color {
        background-color: var(--advancedSearchHeaderColor);
    }
    .c-search-field {
        width: 25% !important;
    }
    .c-select {
        width: 50px !important;
    }
    .c-card-text {
        height: 300px !important;
    }
    .document--list--item {
        list-style: none;
        border-bottom: 1px solid #ddd;
        box-sizing: border-box;

    }
    .width-sm {
        width: 100px;
    }

    /* Fix color contrast for "No data available" text for WCAG AA compliance */
    .v-data-table >>> td[colspan] {
        color: #757575 !important; /* Darker gray for better contrast */
    }

</style>